import type { HistoryItemDetail, HistoryListItem } from "@/composables/ai";
import { useAi } from "@/composables/ai";
import type { CurrencyId } from "@/services/currencies";
import type { Image } from "@/services/images";
import { factoryImage } from "@/services/images";
import { IndexedDb, IndexedDbKey } from "@/services/indexedDb";
import type { PriceNumeric } from "@/services/prices";
import { isNumericPrice } from "@/services/prices";
import type { Parameters } from "@/services/collection/parameters";
import { CategoryId } from "@/services/collection/categories/categoriesTypes";

export const SupportedCategoryIds = [
  CategoryId.LegoSets, //
  CategoryId.LegoMinifigures,
  CategoryId.SportsCards,
  CategoryId.VinylRecords,
  CategoryId.BoardGames,
  CategoryId.Pokemon,
  CategoryId.Watches,
];

export const getCoverImage = (item: HistoryListItem): Image | undefined => {
  const img = item.images.length > 0 ? item.images[0] : undefined;
  if (img) {
    return factoryImage(0 /* fake ID */, img.imageUrl, item.name);
  } else {
    return undefined;
  }
};

export const getImages = (item: HistoryListItem | HistoryItemDetail): Image[] => item.images.map(({ imageUrl }) => factoryImage(0 /* fake ID */, imageUrl, item.name));

export const getParamValue = (param: Parameters.Parameter, item: HistoryItemDetail): string | undefined => item.parameters.find((p) => p.parameterId === param.id)?.value;

export const hasPrice = (item: HistoryListItem | HistoryItemDetail): item is (HistoryItemDetail | HistoryItemDetail) & { priceFrom: PriceNumeric; priceTo: PriceNumeric } => isNumericPrice(item.priceFrom) && isNumericPrice(item.priceTo);

type PriceRange = { from: number; to: number; currencyId: CurrencyId };
export const getPriceRange = (item: HistoryListItem | HistoryItemDetail): PriceRange => {
  if (!isNumericPrice(item.priceFrom) || !isNumericPrice(item.priceTo)) {
    throw new Error("DEV: Should not happen. Cannot get price range for item without price.");
  }
  if (item.priceFrom.currencyId !== item.priceTo.currencyId) {
    throw new Error("DEV: Should not happen. Price range currencyId mismatch.");
  }

  return {
    from: item.priceFrom.price,
    to: item.priceTo.price,
    currencyId: item.priceFrom.currencyId,
  };
};

export const saveLastCategoryId = async (id: CategoryId) => {
  useAi().storeLastCategoryIdIntoRef(id);
  return IndexedDb.set(IndexedDbKey.AiLastCategoryId, id);
};
export const getLastCategoryId = async (): Promise<CategoryId | undefined> => {
  const id = Number(await IndexedDb.get(IndexedDbKey.AiLastCategoryId));
  useAi().storeLastCategoryIdIntoRef(id);
  return id;
};

export const FormAnchor = "AiForm";
